// Auditoría: publicaciones, pausas y ediciones de todas las reglas, juntas y ordenables.
// Mockup: portal-admin-vl-comisiones.html
import { useState } from 'react';
import type { RetailCommission } from '@fiado/type-kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { CellLines, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { LifecycleEventBadge, toLifecycleEvent } from '@/shared/patterns/Lifecycle';
import { ScreenFrameNotice } from '@/shared/patterns/ScreenFrame';
import { useRuleHistories } from '../../api/commissionQueries';
import {
    mergeHistories,
    nextAuditSort,
    sortAudit,
    type AuditRow,
    type AuditSort,
    type AuditSortKey,
} from '../../lib/ruleAudit';

const COLUMNS: DataTableColumn<AuditRow, AuditSortKey>[] = [
    {
        key: 'at',
        label: 'Fecha y hora',
        width: '150px',
        sortKey: 'at',
        cell: (row) => formatDateTimeMx(row.at),
    },
    {
        key: 'author',
        label: 'Autor',
        width: '170px',
        sortKey: 'performedBy',
        cellTitle: (row) => row.performedBy,
        cell: (row) => <span className="font-mono text-[10.5px]">{row.performedBy}</span>,
    },
    {
        key: 'action',
        label: 'Evento',
        width: '96px',
        sortKey: 'action',
        cell: (row) => (
            <LifecycleEventBadge event={toLifecycleEvent(row.action)} label={row.action} />
        ),
    },
    {
        key: 'rule',
        label: 'Regla',
        sortKey: 'rule',
        cell: (row) => <CellLines primary={row.ruleName} secondary={row.ruleId} />,
    },
    {
        key: 'version',
        label: 'Versión',
        width: '72px',
        numeric: true,
        cell: (row) => (row.version === null ? '—' : `v${String(row.version)}`),
    },
];

export function RulesAuditTab({ rules }: { rules: RetailCommission.CommissionRule[] }) {
    const histories = useRuleHistories(rules.map((rule) => rule.ruleId));
    const [sort, setSort] = useState<AuditSort>({ key: 'at', direction: 'desc' });
    const failed = histories.filter((history) => history.isError).length;
    const rows = sortAudit(
        mergeHistories(
            rules.map((rule, index) => ({ rule, events: histories.at(index)?.data?.events ?? [] })),
        ),
        sort,
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <p className="flex-none px-4 py-2 text-[11px] text-muted-foreground">
                Historial de publicaciones, pausas y ediciones de las reglas. Los cambios de
                políticas y las reglas eliminadas no aparecen aquí.
            </p>
            {failed > 0 && (
                <ScreenFrameNotice tone="warning">
                    No se pudo leer el historial de {failed} regla(s).
                </ScreenFrameNotice>
            )}
            <div className="min-h-0 flex-1 overflow-auto">
                <DataTable
                    columns={COLUMNS}
                    rows={rows}
                    rowKey={(row) => row.key}
                    caption="Auditoría de reglas"
                    sort={sort}
                    onSortToggle={(key) => {
                        setSort(nextAuditSort(sort, key));
                    }}
                    isLoading={histories.some((history) => history.isLoading)}
                    empty="Sin eventos de reglas registrados."
                    density="compact"
                />
            </div>
        </div>
    );
}
